import { useCallback, useEffect, useMemo, useState } from "react"

import * as Tabs from "@radix-ui/react-tabs"

import AccountingGroupSelector from "./AccountingGroupSelector"
import styles from "./AccountingTab.module.scss"
import { useCreateAccountingGroupModal } from "./CreateAccountingGroupModal"
import { useEditMappingModal } from "./EditMappingModal"
import AdjustmentSubTab from "./adjustment/AdjustmentSubTab"
import OverviewSubTab from "./overview/OverviewSubTab"
import SettlementSubTab from "./settlement/SettlementSubTab"

import { getFragmentData, graphql } from "@/company_admin/gql/__generated__"
import { useIsSouhlAdmin } from "@/company_admin/hooks/useIsSouhlAdmin"
import { useCompanyAdminQuery } from "@/provider/apollo/Apollo"

// --- Settlement Query (lifted here for refetch access) ---

const GetSettlements = graphql(/* GraphQL */ `
	query GetSettlements($param: SettlementsParam!) {
		settlements(param: $param) {
			settlements {
				...SettlementFields
			}
		}
	}
`)

// --- Fragments ---

const accountingGroupFragment = graphql(/* GraphQL */ `
	fragment AccountingGroupFields on AccountingGroup {
		id
		companyId
		name
		displayName
		bankCode
		bankBranchCode
		bankAccountType
		bankAccountNumber
		bankAccountHolderName
		billingEmails
		createTime
		updateTime
	}
`)

const accountingGroupSalonFragment = graphql(/* GraphQL */ `
	fragment AccountingGroupSalonFields on AccountingGroupSalon {
		salonId
		salonName
		isArchived
	}
`)

// --- Queries ---

const GetAccountingGroups = graphql(/* GraphQL */ `
	query GetAccountingGroups($param: GetAccountingGroupsParam!) {
		getAccountingGroups(param: $param) {
			accountingGroups {
				...AccountingGroupFields
			}
		}
	}
`)

const GetAccountingGroupDetail = graphql(/* GraphQL */ `
	query GetAccountingGroupDetail($param: GetAccountingGroupDetailParam!) {
		getAccountingGroupDetail(param: $param) {
			accountingGroup {
				...AccountingGroupFields
			}
			salons {
				...AccountingGroupSalonFields
			}
			souhlUnpaidAmount
			companyUnpaidAmount
			unpaidUntil {
				year
				month
			}
		}
	}
`)

enum SubTab {
	OVERVIEW = "OVERVIEW",
	SETTLEMENT = "SETTLEMENT",
	ADJUSTMENT = "ADJUSTMENT",
}

const AccountingTab = () => {
	const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null)
	const { isSouhlAdmin } = useIsSouhlAdmin()

	// 経理グループ一覧取得
	const { data: groupsData, refetch: refetchGroups } = useCompanyAdminQuery(
		GetAccountingGroups,
		{
			variables: { param: {} },
		}
	)

	const accountingGroups = useMemo(() => {
		if (!groupsData?.getAccountingGroups?.accountingGroups) return []
		return getFragmentData(
			accountingGroupFragment,
			groupsData.getAccountingGroups.accountingGroups
		)
	}, [groupsData])

	// 初回ロード時 or グループ一覧変更時に先頭を選択（選択中グループが削除された場合もフォールバック）
	useEffect(() => {
		if (accountingGroups.length === 0) {
			setSelectedGroupId(null)
			return
		}
		const currentExists = accountingGroups.some((g) => g.id === selectedGroupId)
		if (!selectedGroupId || !currentExists) {
			setSelectedGroupId(accountingGroups[0].id)
		}
	}, [accountingGroups, selectedGroupId])

	// 選択中グループの詳細取得
	const { data: detailData, refetch: refetchDetail } = useCompanyAdminQuery(
		GetAccountingGroupDetail,
		{
			variables: {
				param: { accountingGroupId: selectedGroupId ?? "" },
			},
			skip: !selectedGroupId,
		}
	)

	// 相殺明細取得
	const {
		data: settlementsData,
		loading: settlementsLoading,
		error: settlementsError,
		refetch: refetchSettlements,
	} = useCompanyAdminQuery(GetSettlements, {
		variables: { param: { accountingGroupId: selectedGroupId ?? "" } },
		skip: !selectedGroupId,
		fetchPolicy: "network-only",
	})

	const selectedGroup = useMemo(() => {
		if (!detailData?.getAccountingGroupDetail?.accountingGroup) return null
		return getFragmentData(
			accountingGroupFragment,
			detailData.getAccountingGroupDetail.accountingGroup
		)
	}, [detailData])

	const selectedGroupSalons = useMemo(() => {
		if (!detailData?.getAccountingGroupDetail?.salons) return []
		return getFragmentData(
			accountingGroupSalonFragment,
			detailData.getAccountingGroupDetail.salons
		).filter((s) => !s.isArchived)
	}, [detailData])

	const handleGroupCreated = useCallback((newGroupId: string) => {
		setSelectedGroupId(newGroupId)
	}, [])

	const createModal = useCreateAccountingGroupModal({
		onCreated: handleGroupCreated,
		refetchGroups,
	})

	const mappingGroups = useMemo(
		() => accountingGroups.map((g) => ({ id: g.id, name: g.name })),
		[accountingGroups]
	)

	const mappingModal = useEditMappingModal({
		accountingGroups: mappingGroups,
		refetchGroups,
		refetchDetail,
		refetchSettlements,
	})

	return (
		<div className={styles.accounting_tab}>
			<AccountingGroupSelector
				accountingGroups={mappingGroups}
				selectedGroupId={selectedGroupId}
				onSelectGroup={setSelectedGroupId}
				onClickCreate={createModal.openModal}
				onClickEditMapping={mappingModal.openModal}
				isSouhlAdmin={isSouhlAdmin}
			/>

			{selectedGroup && (
				<Tabs.Root defaultValue={SubTab.OVERVIEW}>
					<Tabs.List className={styles.sub_tabs}>
						<Tabs.Trigger value={SubTab.OVERVIEW} className={styles.sub_tab}>
							概要
						</Tabs.Trigger>
						<Tabs.Trigger value={SubTab.SETTLEMENT} className={styles.sub_tab}>
							相殺明細
						</Tabs.Trigger>
						<Tabs.Trigger value={SubTab.ADJUSTMENT} className={styles.sub_tab}>
							調整
						</Tabs.Trigger>
					</Tabs.List>

					<Tabs.Content value={SubTab.OVERVIEW}>
						<OverviewSubTab
							accountingGroup={selectedGroup}
							salonsRaw={selectedGroupSalons}
							souhlUnpaidAmount={
								detailData?.getAccountingGroupDetail?.souhlUnpaidAmount ?? 0
							}
							companyUnpaidAmount={
								detailData?.getAccountingGroupDetail?.companyUnpaidAmount ?? 0
							}
							unpaidUntil={
								detailData?.getAccountingGroupDetail?.unpaidUntil ?? null
							}
							refetchDetail={refetchDetail}
							refetchGroups={refetchGroups}
							refetchSettlements={refetchSettlements}
						/>
					</Tabs.Content>
					<Tabs.Content value={SubTab.SETTLEMENT}>
						<SettlementSubTab
							settlements={settlementsData?.settlements?.settlements ?? []}
							loading={settlementsLoading}
							error={settlementsError}
							refetchSettlements={refetchSettlements}
							isSouhlAdmin={isSouhlAdmin}
							accountingGroupId={selectedGroup.id}
						/>
					</Tabs.Content>
					<Tabs.Content value={SubTab.ADJUSTMENT}>
						<AdjustmentSubTab
							key={selectedGroup.id}
							accountingGroupId={selectedGroup.id}
							isSouhlAdmin={isSouhlAdmin}
							refetchSettlements={refetchSettlements}
						/>
					</Tabs.Content>
				</Tabs.Root>
			)}

			{!selectedGroup && accountingGroups.length === 0 && (
				<div className={styles.empty_state}>
					<p>経理グループがまだ作成されていません。</p>
					<p>「+」ボタンから経理グループを作成してください。</p>
				</div>
			)}

			{createModal.render()}
			{mappingModal.render()}
		</div>
	)
}

export default AccountingTab
